import { del, get, post, put } from '@/lib/api';
import { cleanObject } from '@shared/utils/cleanObject.js';

export const getWorkflow = ({ id, workflowVersion, isPublished = false, cancelRef }, callback) => {
  return get(`v1/workflows/${id}/${workflowVersion}`, {
    action: 'GET_WORKFLOW',
    loader: 'getWorkflow',
    callback,
    query: { isPublished },
    meta: { isPublished },
    cancelRef
  });
};

export const getWorkflowState = ({ id, workflowVersion, environmentId, integrationId, endUserId, cancelRef }, callback) => {
  return get(`v1/workflows/${id}/${workflowVersion}/state`, {
    action: 'GET_WORKFLOW_STATE',
    loader: 'getWorkflow',
    callback,
    query: { environmentId, integrationId, endUserId },
    meta: {},
    cancelRef
  });
};

export const getActiveWorkflowsByCredentialId = ({ credentialId }, callback) => {
  return get(`v1/workflows/credential/${credentialId}`, {
    action: 'GET_ACTIVE_WORKFLOWS_BY_CREDENTIAL_ID',
    loader: 'getActiveWorkflowsByCredentialId',
    callback
  });
};

export const getWorkflowStepTriggerData = ({ workflowId, workflowVersion, stepId }, callback) => {
  return get(`v1/workflows/${workflowId}/${workflowVersion}/steps/${stepId}/trigger-data`, {
    action: 'GET_WORKFLOW_STEP_TRIGGER_DATA',
    loader: `getWorkflowStepTriggerData`,
    callback
  });
};

export const getAllPublishedWorkflows = (callback) => {
  return get(`v1/workflows/allCompanies`, {
    loader: 'getAllPublishedWorkflows',
    action: 'GET_ALL_PUBLISHED_WORKFLOWS',
    callback
  });
};

export const getWorkflowsByUsage = ({ appId, actionId }, callback) => {
  return get(`v1/apps/${appId}/usage`, {
    query: { actionId },
    loader: 'getWorkflowsByUsage',
    callback
  });
};

export const getCredentials = ({ companyId }, callback) => {
  return get(`v1/credentials`, {
    query: { companyId },
    loader: 'getCredentials',
    action: 'GET_CREDENTIALS',
    callback
  });
};

export const getCredentialsByCompany = ({ companyId }, callback) => {
  return get(`v1/credentials`, {
    query: { companyId },
    loader: `getCredentials-${companyId}`,
    action: 'GET_CREDENTIALS_BY_COMPANY',
    callback,
    meta: { companyId }
  });
};

export const searchCredentials = ({ search, appId }, callback) => {
  return get(`v1/credentials/search`, {
    query: { search, appId },
    loader: 'searchCredentials',
    action: 'SEARCH_CREDENTIALS',
    callback
  });
};

export const getCredentialById = (id, { companyId, secrets, useCache, loader, forceUpdate }, callback) => {
  return get(`v1/credentials/${id}`, {
    query: {
      secrets,
      companyId,
      useCache
    },
    loader,
    action: 'GET_CREDENTIAL_BY_ID',
    callback,
    meta: { forceUpdate }
  });
};

export const getPlatformSecrets = ({ appId, search }, callback) => {
  return get(`v1/platform-secrets`, {
    query: { appId, search },
    loader: `getPlatformSecrets-${appId}`,
    action: 'GET_PLATFORM_SECRETS',
    callback,
    meta: { appId }
  });
};

export const getPlatformSecretById = (id, { loader }, callback) => {
  return get(`v1/platform-secrets/${id}`, {
    loader,
    action: 'GET_PLATFORM_SECRET_BY_ID',
    callback,
    meta: { id }
  });
};

export const getCredentialByIdEmbedded = (id, { companyId, loader, embeddedToken }, callback) => {
  return get(`v1/credentials/${id}/embedded`, {
    query: {
      companyId
    },
    headers: {
      Authorization: `Bearer ${embeddedToken}`
    },
    loader,
    action: 'GET_CREDENTIAL_BY_ID',
    callback
  });
};

export const postRunWorkflowStep = ({ workflowId, stepId, layer, environmentId, installationId }, callback) => {
  return post(`v1/workflows/${workflowId}/steps/${stepId}/run`, {
    data: { layer, environmentId, installationId },
    action: 'POST_RUN_WORKFLOW_STEP',
    callback,
    meta: { workflowId, stepId, environmentId, installationId }
  });
};

export const postCancelWorkflowStep = ({ workflowId, stepId, layer }, callback) => {
  return post(`v1/workflows/${workflowId}/steps/${stepId}/cancel`, {
    action: 'POST_CANCEL_WORKFLOW_STEP',
    meta: { workflowId, stepId },
    callback
  });
};

export const postReplayWebhookTrigger = ({ workflowId, workflowVersion, stepId }, callback) => {
  return post(`v1/workflows/${workflowId}/${workflowVersion}/steps/${stepId}/replay-trigger`, {
    action: 'POST_REPLAY_WEBHOOK_TRIGGER',
    meta: { workflowId, workflowVersion, stepId },
    callback
  });
};

export const testWorkflowStepCredential = ({ workflowId, stepId }, callback) => {
  return post(`v1/workflows/${workflowId}/steps/${stepId}/credential-test`, {
    data: { workflowId, stepId },
    action: 'POST_TEST_WORKFLOW_STEP_CREDENTIAL',
    callback,
    meta: { workflowId, stepId }
  });
};

export const testWebhook = ({ url, method, queryParams, contentType, body }, callback) => {
  return post('v1/workflows/test-webhook', {
    data: { url, method, queryParams, contentType, body },
    action: 'TEST_WEBHOOK',
    callback
  });
};

export const postRunMultipleWorkflowSteps = ({ workflowId, stepIds, environmentId, installationId }, callback) => {
  return post(`v1/workflows/${workflowId}/steps/run`, {
    data: { stepIds, environmentId, installationId },
    action: 'POST_RUN_MULTIPLE_WORKFLOW_STEPS',
    callback,
    meta: { workflowId, stepIds }
  });
};

export const postRunWorkflow = ({ workflowId, environmentId, force, runPublished, installationId }, callback) => {
  return post(`v1/workflows/${workflowId}/run`, {
    data: { force, runPublished, environmentId, installationId },
    action: 'POST_RUN_WORKFLOW',
    callback,
    meta: { workflowId }
  });
};

export const replayWorkflowAction = ({ workflowId, workflowLogId, startStepId, endStepId, startAction, endAction }, callback) => {
  return post(`v1/workflows/${workflowId}/run`, {
    data: { replay: { workflowLogId, startStepId, endStepId, startAction, endAction } },
    action: 'REPLAY_WORKFLOW_ACTION',
    loader: 'replayWorkflowAction',
    callback,
    meta: { workflowId, workflowLogId, startStepId, endStepId }
  });
};

export const postRunAppAction = (data, callback, onResponse) => {
  return post(`v1/apps/${data.appId}/${data.appVersion}/action/${data.actionId}/run`, {
    action: data?.lookup ? 'POST_RUN_APP_ACTION_LOOKUP' : typeof data?.transform === 'string' ? null : 'POST_RUN_APP_ACTION',
    data: {
      transform: data?.transform,
      lookup: data?.lookup,
      lookupSettings: data?.lookupSettings,
      cache: data?.cache,
      useAdminCredentials: data?.useAdminCredentials,
      action: {
        appId: data?.appId,
        appVersion: data?.appVersion,
        actionId: data?.actionId,
        stepId: data?.stepId,
        credentialId: data?.credentialId,
        arguments: data?.arguments,
        options: data?.options,
        companyId: data?.companyId,
        workflowId: data?.workflowId
      },
      isTutorial: data?.isTutorial
    },
    callback,
    onResponse,
    loader: data?.lookup ? 'postRunAppLookupAction' : 'postRunAppAction',
    meta: {
      step: data,
      actionId: data?.actionId,
      appId: data?.appId
    }
  });
};

export const getApps = (query, callback) => {
  return get(`v1/apps`, {
    action: 'GET_APPS',
    loader: 'appVersions',
    query,
    callback
  });
};

export const getAppsMetadata = (query, callback) => {
  return get(`v1/apps/metadata`, {
    action: 'GET_APPS_METADATA',
    loader: 'getAppsMetadata',
    query,
    callback
  });
};

export const getAppQualityReviewSummary = (callback) => {
  return get(`v1/apps/quality-review/summary`, {
    action: 'GET_APP_QUALITY_REVIEW_SUMMARY',
    loader: 'getAppQualityReviewSummary',
    callback
  });
};

export const getAppQualityReviewRecentByKey = (reviewKey, callback) => {
  return get(`v1/apps/quality-review/recent/${encodeURIComponent(reviewKey)}`, {
    action: 'GET_APP_QUALITY_REVIEW_RECENT_BY_KEY',
    loader: 'getAppQualityReviewRecentByKey',
    callback
  });
};

export const getAppQualityReviewRemainingApps = (reviewKey, callback) => {
  return get(`v1/apps/quality-review/remaining/${encodeURIComponent(reviewKey)}`, {
    action: 'GET_APP_QUALITY_REVIEW_REMAINING_APPS',
    loader: 'getAppQualityReviewRemainingApps',
    callback
  });
};

export const getAppsQualityReviewOverview = (callback) => {
  return get(`v1/apps/quality-review`, {
    action: 'GET_APPS_QUALITY_REVIEW_OVERVIEW',
    loader: 'getAppsQualityReviewOverview',
    callback
  });
};

export const getAppsQualityReviewMissingSetupGuide = (callback) => {
  return get(`v1/apps/quality-review/missing-setup-guide`, {
    action: 'GET_APPS_QUALITY_REVIEW_MISSING_SETUP_GUIDE',
    loader: 'getAppsQualityReviewMissingSetupGuide',
    callback
  });
};

export const getAppsQualityReviewMissingLogos = (callback) => {
  return get(`v1/apps/quality-review/missing-logos`, {
    action: 'GET_APPS_QUALITY_REVIEW_MISSING_LOGOS',
    loader: 'getAppsQualityReviewMissingLogos',
    callback
  });
};

export const getAppsQualityReviewMissingDescriptions = (callback) => {
  return get(`v1/apps/quality-review/missing-descriptions`, {
    action: 'GET_APPS_QUALITY_REVIEW_MISSING_DESCRIPTIONS',
    loader: 'getAppsQualityReviewMissingDescriptions',
    callback
  });
};

export const getAppsQualityReviewMissingApplicationClass = (callback) => {
  return get(`v1/apps/quality-review/missing-application-class`, {
    action: 'GET_APPS_QUALITY_REVIEW_MISSING_APPLICATION_CLASS',
    loader: 'getAppsQualityReviewMissingApplicationClass',
    callback
  });
};

export const getAppsQualityReviewHiddenApps = (callback) => {
  return get(`v1/apps/quality-review/hidden-apps`, {
    action: 'GET_APPS_QUALITY_REVIEW_HIDDEN_APPS',
    loader: 'getAppsQualityReviewHiddenApps',
    callback
  });
};

export const getAppsQualityReviewResponseSchemaIssues = (callback) => {
  return get(`v1/apps/quality-review/response-schema-issues`, {
    action: 'GET_APPS_QUALITY_REVIEW_RESPONSE_SCHEMA_ISSUES',
    loader: 'getAppsQualityReviewResponseSchemaIssues',
    callback
  });
};

export const getAppsQualityReviewResponseSchemaIssueActions = ({ appId }, callback) => {
  return get(`v1/apps/quality-review/response-schema-issues/${appId}/actions`, {
    action: 'GET_APPS_QUALITY_REVIEW_RESPONSE_SCHEMA_ISSUE_ACTIONS',
    loader: 'getAppsQualityReviewResponseSchemaIssueActions',
    callback,
    meta: { appId }
  });
};

export const getAppsQualityReviewResultSummarizerIssues = (callback) => {
  return get(`v1/apps/quality-review/result-summarizer-issues`, {
    action: 'GET_APPS_QUALITY_REVIEW_RESULT_SUMMARIZER_ISSUES',
    loader: 'getAppsQualityReviewResultSummarizerIssues',
    callback
  });
};

export const getAppsQualityReviewResultSummarizerIssueActions = ({ appId }, callback) => {
  return get(`v1/apps/quality-review/result-summarizer-issues/${appId}/actions`, {
    action: 'GET_APPS_QUALITY_REVIEW_RESULT_SUMMARIZER_ISSUE_ACTIONS',
    loader: 'getAppsQualityReviewResultSummarizerIssueActions',
    callback,
    meta: { appId }
  });
};

export const getAppsQualityReviewMissingRecordType = (callback) => {
  return get(`v1/apps/quality-review/missing-record-type`, {
    action: 'GET_APPS_QUALITY_REVIEW_MISSING_RECORD_TYPE',
    loader: 'getAppsQualityReviewMissingRecordType',
    callback
  });
};

export const getAppsQualityReviewMissingRecordTypeActions = ({ appId }, callback) => {
  return get(`v1/apps/quality-review/missing-record-type/${appId}/actions`, {
    action: 'GET_APPS_QUALITY_REVIEW_MISSING_RECORD_TYPE_ACTIONS',
    loader: 'getAppsQualityReviewMissingRecordTypeActions',
    callback,
    meta: { appId }
  });
};

export const getAppsQualityReviewMissingOperation = (callback) => {
  return get(`v1/apps/quality-review/missing-operation`, {
    action: 'GET_APPS_QUALITY_REVIEW_MISSING_OPERATION',
    loader: 'getAppsQualityReviewMissingOperation',
    callback
  });
};

export const getAppsQualityReviewMissingOperationActions = ({ appId }, callback) => {
  return get(`v1/apps/quality-review/missing-operation/${appId}/actions`, {
    action: 'GET_APPS_QUALITY_REVIEW_MISSING_OPERATION_ACTIONS',
    loader: 'getAppsQualityReviewMissingOperationActions',
    callback,
    meta: { appId }
  });
};

export const getAppsQualityReviewDirtyDrafts = (callback) => {
  return get(`v1/apps/quality-review/dirty-drafts`, {
    action: 'GET_APPS_QUALITY_REVIEW_DIRTY_DRAFTS',
    loader: 'getAppsQualityReviewDirtyDrafts',
    callback
  });
};

export const getAppsFeaturesOverview = (callback) => {
  return get(`v1/apps/features`, {
    action: 'GET_APPS_FEATURES_OVERVIEW',
    loader: 'getAppsFeaturesOverview',
    callback
  });
};

export const getAppsFeaturesDetail = ({ featureKey }, callback) => {
  return get(`v1/apps/features/${featureKey}`, {
    action: 'GET_APPS_FEATURES_DETAIL',
    loader: 'getAppsFeaturesDetail',
    callback,
    meta: { featureKey }
  });
};

export const getAppsStatsOverview = (callback) => {
  return get(`v1/apps/stats`, {
    action: 'GET_APPS_STATS_OVERVIEW',
    loader: 'getAppsStatsOverview',
    callback
  });
};

export const getAppLogoColors = ({ search, limit, offset, cancelRef } = {}, callback) => {
  return get(`v1/apps/app-logo-colors`, {
    action: 'GET_APP_LOGO_COLORS',
    loader: 'getAppLogoColors',
    query: { search, limit, offset },
    callback,
    cancelRef
  });
};

export const publishAppLogoColor = ({ appId, appBackgroundEnabled, appBackgroundProperties }, callback) => {
  return post(`v1/apps/app-logo-colors/publish`, {
    action: 'PUBLISH_APP_LOGO_COLOR',
    loader: `publishAppLogoColor-${appId}`,
    data: { appId, appBackgroundEnabled, appBackgroundProperties },
    meta: { appId },
    callback
  });
};

export const getAdminAiUsageDashboard = ({ range = '30d', startDate, endDate } = {}, callback) => {
  const query = { range };
  if (startDate) {
    query.startDate = startDate;
  }
  if (endDate) {
    query.endDate = endDate;
  }
  return get(`v1/cirra/billing/admin/ai-usage`, {
    action: 'GET_ADMIN_AI_USAGE_DASHBOARD',
    loader: 'getAdminAiUsageDashboard',
    query,
    callback
  });
};

export const getAdminCloudyConversationTranscript = ({ companyId, userId, workflowId } = {}, callback) => {
  return get(`v1/cirra/billing/admin/ai-usage/cloudy/conversations/${encodeURIComponent(companyId)}/${encodeURIComponent(userId)}/${encodeURIComponent(workflowId)}`, {
    action: 'GET_ADMIN_CLOUDY_CONVERSATION_TRANSCRIPT',
    loader: `getAdminCloudyConversationTranscript:${workflowId}`,
    callback
  });
};

export const getAdminCloudyRunTrace = ({ companyId, userId, workflowId, chainHeadRunId, beforeRunCreatedOn, beforeRunId, beforeSeq, maxBytes } = {}, callback) => {
  const query = {};
  if (chainHeadRunId) query.chainHeadRunId = chainHeadRunId;
  if (beforeRunCreatedOn) query.beforeRunCreatedOn = beforeRunCreatedOn;
  if (beforeRunId) query.beforeRunId = beforeRunId;
  if (beforeSeq != null) query.beforeSeq = String(beforeSeq);
  if (maxBytes != null) query.maxBytes = String(maxBytes);
  return get(`v1/cirra/billing/admin/ai-usage/cloudy/traces/${encodeURIComponent(companyId)}/${encodeURIComponent(userId)}/${encodeURIComponent(workflowId)}`, {
    action: 'GET_ADMIN_CLOUDY_RUN_TRACE',
    loader: `getAdminCloudyRunTrace:${workflowId}`,
    query,
    callback
  });
};

export const postRefreshAppsMetadata = (callback) => {
  return post(`v1/apps/metadata/refresh`, {
    action: 'POST_REFRESH_APPS_METADATA',
    loader: 'postRefreshAppsMetadata',
    data: {},
    callback
  });
};

export const getAppByIdAndVersion = ({ appId, appVersion }, callback) => {
  return get(`v1/apps/${appId}/${appVersion}`, {
    action: 'GET_APP',
    callback
  });
};

export const getAppVersions = ({ appId }, callback) => {
  return get(`v1/apps/${appId}/versions`, {
    action: 'GET_APP_VERSIONS',
    loader: 'getAppVersions',
    callback,
    meta: { appId }
  });
};

export const getAppActionsUsage = ({ appId }, callback) => {
  return get(`v1/apps/${appId}/actions/usage`, {
    action: 'GET_APP_ACTIONS_USAGE',
    loader: 'getAppActionsUsage',
    callback
  });
};

export const getAppActionOutputSummary = ({ appId }, callback) => {
  return get(`v1/apps/${appId}/actions/output-summary`, {
    action: 'GET_APP_ACTION_OUTPUT_SUMMARY',
    loader: 'getAppActionOutputSummary',
    callback
  });
};

export const getAppAuthUsage = ({ appId }, callback) => {
  return get(`v1/apps/${appId}/auth/usage`, {
    action: 'GET_APP_AUTH_USAGE',
    loader: 'getAppAuthUsage',
    callback
  });
};

export const postApp = (payload = {}, callback) => {
  return post(`v1/apps`, {
    data: {
      ...payload
    },
    action: 'POST_APP',
    callback
  });
};

export const postAutoLoadAppLogo = ({ appId, appVersion }, callback) => {
  return post(`v1/apps/${appId}/${appVersion}/logo/auto-load`, {
    action: 'POST_AUTO_LOAD_APP_LOGO',
    loader: 'postAutoLoadAppLogo',
    data: {},
    callback,
    meta: {
      appId,
      appVersion
    }
  });
};

export const postRefreshAppWebsiteContent = ({ appId, appVersion, contentKeys, targetAppSlug, targetAppVersion }, callback) => {
  return post(`v1/apps/${appId}/${appVersion}/website-content/refresh`, {
    action: 'POST_REFRESH_APP_WEBSITE_CONTENT',
    loader: 'postRefreshAppWebsiteContent',
    data: {
      contentKeys,
      targetAppSlug,
      targetAppVersion
    },
    callback,
    meta: {
      appId,
      appVersion,
      targetAppSlug,
      targetAppVersion
    }
  });
};

export const getAppWebsiteContentStatus = ({ appId, appVersion, targetAppSlug }, callback) => {
  return get(`v1/apps/${appId}/${appVersion}/website-content/status`, {
    action: 'GET_APP_WEBSITE_CONTENT_STATUS',
    loader: 'getAppWebsiteContentStatus',
    query: {
      targetAppSlug
    },
    callback,
    meta: {
      appId,
      appVersion,
      targetAppSlug
    }
  });
};

export const postGenerateAppWebsiteContent = ({ appId, appVersion, contentKey, targetAppSlug, targetAppVersion }, callback) => {
  return post(`v1/apps/${appId}/${appVersion}/website-content/${contentKey}/generate`, {
    action: 'POST_GENERATE_APP_WEBSITE_CONTENT',
    loader: 'postGenerateAppWebsiteContent',
    data: {
      targetAppSlug,
      targetAppVersion
    },
    callback,
    meta: {
      appId,
      appVersion,
      contentKey,
      targetAppSlug,
      targetAppVersion
    }
  });
};

export const getStepActionMockData = ({ workflowId, stepId, actionId }, callback) => {
  return get(`v1/workflows/${workflowId}/steps/${stepId}/${actionId}/response/mock`, {
    action: 'GET_APP_MOCK_RESPONSE',
    callback,
    meta: {
      stepId,
      workflowId,
      actionId
    }
  });
};

export const getWorkflowResponses = ({ workflowId, stepIds, search, cancelRef }, callback) => {
  return get(`v1/workflows/${workflowId}/responses`, {
    action: 'GET_WORKFLOW_RESPONSES',
    loader: 'getWorkflowResponses',
    query: cleanObject({
      stepIds: Array.isArray(stepIds) ? stepIds.join(',') : stepIds,
      search
    }),
    callback,
    cancelRef
  });
};

export const getStepActionResponses = ({ workflowId, stepId, actionId, outputPage, outputId, search, format, activeIndex, stepOutputId, cancelRef }, callback) => {
  return get(`v1/workflows/${workflowId}/steps/${stepId}/${actionId}/response`, {
    query: cleanObject({
      outputPage,
      outputId,
      format,
      search: search || undefined,
      activeIndex
    }),
    loader: 'getStepActionResponses',
    action: 'GET_STEP_ACTION_RESPONSES',
    callback,
    cancelRef,
    meta: {
      stepId,
      outputPage,
      stepOutputId
    }
  });
};

export const getWorkflowSteps = (query, forceUpdate = false, callback) => {
  return get(`v1/workflows/steps`, {
    action: 'GET_WORKFLOW_STEPS',
    query,
    callback,
    meta: { forceUpdate }
  });
};

export const getWorkflows = (query, callback) => {
  return get(`v1/workflows`, {
    action: 'GET_WORKFLOWS',
    loader: 'getWorkflows',
    query,
    callback
  });
};

export const getWorkflowsMetadata = (query, callback) => {
  return get(`v1/workflows/metadata`, {
    action: 'GET_WORKFLOWS_METADATA',
    query,
    callback
  });
};

export const getAppActionOutput = ({ appId, appVersion, actionId, mapperId, allMappers, mapper, cancelRef, callback }) => {
  return get(`v1/apps/${appId}/${appVersion}/outputs/${actionId}`, {
    action: 'GET_APP_ACTION_OUTPUT',
    loader: 'getAppActionOutput',
    query: { allMappers, mapper, mapperId },
    cancelRef,
    callback
  });
};

export const getAppActionOutboundOutput = ({ appId, appVersion, actionId, mapperId, allMappers, mapper, credentialId, cancelRef, callback }) => {
  return get(`v1/apps/${appId}/${appVersion}/outputs/${actionId}/outbound`, {
    action: 'GET_APP_ACTION_OUTBOUND_OUTPUT',
    loader: 'getAppActionOutboundOutput',
    query: { allMappers, mapper, mapperId, credentialId },
    cancelRef,
    callback
  });
};

export const getAppActionSampleOutputs = ({ appId, actionId, callback }) => {
  return get(`v1/apps/${appId}/${actionId}/sample-outputs`, {
    action: 'GET_APP_ACTION_SAMPLE_OUTPUTS',
    loader: 'getAppActionSampleOutputs',
    callback
  });
};

export const getStepOutputFromTimeMachine = ({ workflowLogId, actionId, callback }) => {
  return get(`v1/apps/step-output/${workflowLogId}/${actionId}`, {
    action: 'GET_STEP_OUTPUT_FROM_TIME_MACHINE',
    loader: 'getStepOutputFromTimeMachine',
    callback
  });
};

export const postMapperEval = ({ code, input, inputLabel, opts, normalize, callback }) => {
  return post(`v1/mapper/eval`, {
    action: 'POST_MAPPER_EVAL',
    data: { code, input, inputLabel, normalize, opts },
    callback
  });
};

export const getMapperAvailableLibs = ({ jwt = false, callback }) => {
  return get(`v1/mapper/libs`, {
    action: 'GET_MAPPER_AVAILABLE_LIBS',
    query: { jwt },
    loader: 'getMapperAvailableLibs',
    callback
  });
};

export const getWorkflowLogs = ({ workflowId, dateRangeFilterFrom, dateRangeFilterTo, snapshotFilter, environmentFilter, statusFilter, endUserFilter, installationFilter, activityFilter, search }) => {
  return get(`v1/workflows/${workflowId}/logs`, {
    query: { dateRangeFilterFrom, dateRangeFilterTo, snapshotFilter, environmentFilter, statusFilter, endUserFilter, installationFilter, activityFilter, search },
    loader: 'getWorkflowLogs',
    action: 'GET_WORKFLOW_LOGS'
  });
};

export const getWorkflowRunDetailsLogs = ({ workflowId, dateRangeFilterFrom, dateRangeFilterTo, snapshotFilter, environmentFilter, statusFilter, endUserFilter, installationFilter, activityFilter, search }) => {
  return get(`v1/workflows/${workflowId}/logs`, {
    query: { dateRangeFilterFrom, dateRangeFilterTo, snapshotFilter, environmentFilter, statusFilter, endUserFilter, installationFilter, activityFilter, search },
    loader: 'getWorkflowRunDetailsLogs',
    action: 'GET_WORKFLOW_RUN_DETAILS_LOGS'
  });
};

export const getWorkflowLogsMetadata = ({ workflowId, dateRangeFilterFrom, dateRangeFilterTo, snapshotFilter, environmentFilter, statusFilter, endUserFilter, installationFilter, activityFilter, search }) => {
  return get(`v1/workflows/${workflowId}/logs/metadata`, {
    query: { dateRangeFilterFrom, dateRangeFilterTo, snapshotFilter, environmentFilter, statusFilter, endUserFilter, installationFilter, activityFilter, search },
    loader: 'getWorkflowLogsMetadata',
    action: 'GET_WORKFLOW_LOGS_METADATA'
  });
};

export const getWorkflowLogById = ({ workflowId, workflowLogId }) => {
  return get(`v1/workflows/${workflowId}/logs/${workflowLogId}`, {
    action: 'GET_WORKFLOW_LOG_BY_ID',
    loader: 'getWorkflowLogById',
    meta: {
      logId: workflowLogId
    }
  });
};

export const getWorkflowLogOutput = ({ workflowId, logId }) => {
  return get(`v1/workflows/${workflowId}/logs/${logId}/output`, {
    action: 'GET_WORKFLOW_LOG_OUTPUT',
    loader: 'getWorkflowLogOutput',
    meta: {
      logId
    }
  });
};

export const getWorkflowLogOperationLogs = ({ workflowId, workflowLogId }) => {
  return get(`v1/workflows/${workflowId}/logs/${workflowLogId}/operation-logs`, {
    action: 'GET_WORKFLOW_LOG_OPERATION_LOGS',
    loader: 'getWorkflowLogOperationLogs',
    meta: {
      logId: workflowLogId
    }
  });
};

export const exportWorkflowLogOperationLogs = ({ workflowId, workflowLogId, tab }, callback) => {
  return get(`v1/workflows/${workflowId}/logs/${workflowLogId}/operation-logs/export`, {
    query: { tab },
    action: 'EXPORT_WORKFLOW_LOG_OPERATION_LOGS',
    loader: `exportWorkflowLogOperationLogs-${workflowLogId}-${tab}`,
    callback
  });
};

export const getList = ({ type, format, callback }) => {
  return get(`v1/lists/${type}`, {
    query: { format },
    callback
  });
};

export const getListFormats = ({ type, callback }) => {
  return get(`v1/lists/${type}/formats`, {
    callback
  });
};

export const deleteWorkflowStepResponse = ({ workflowId, stepId, actionId, callback }) => {
  return del(`v1/workflows/${workflowId}/steps/${stepId}/${actionId}/response`, {
    action: 'DELETE_APP_RESPONSE',
    callback,
    meta: {
      stepId
    }
  });
};

export const getWorkflowOperationLogs = (_, callback) => {
  return get(`v1/workflows/operation-logs`, {
    action: 'GET_WORKFLOW_OPERATION_LOGS',
    callback
  });
};

export const getWorkflowOperationLogOutput = ({ id }, callback) => {
  return get(`v1/workflows/operation-logs/${id}`, {
    action: 'GET_WORKFLOW_OPERATION_LOG_OUTPUT',
    meta: { id },
    callback
  });
};

export const duplicateWorkflow = ({ workflowId, companyId, sourceCompanyId, newWorkflowId }, callback) => {
  return post(`v1/workflows/${workflowId}/duplicate`, {
    action: 'DUPLICATE_WORKFLOW',
    query: { companyId, sourceCompanyId, newWorkflowId },
    data: {
      workflowId
    },
    meta: { workflowId, companyId, sourceCompanyId },
    callback
  });
};

export const createFunctionFromSteps = ({ workflowId, stepIds, name }, callback) => {
  return post(`v1/workflows/${workflowId}/function-from-steps`, {
    action: 'CREATE_FUNCTION_FROM_STEPS',
    data: { stepIds, name },
    meta: { workflowId, stepIds },
    callback
  });
};

export const cancelWorkflow = ({ workflowId, workflowLogId }, callback) => {
  return post(`v1/workflows/${workflowId}/logs/${workflowLogId}/cancel`, {
    action: 'CANCEL_WORKFLOW',
    data: {
      workflowId
    },
    meta: { workflowId, workflowLogId },
    callback
  });
};

export const getWorkflowSnapshots = ({ workflowId }, callback) => {
  return get(`v1/workflows/${workflowId}/snapshots`, {
    action: 'GET_WORKFLOW_SNAPSHOTS',
    loader: 'getWorkflowSnapshots',
    meta: {
      workflowId
    },
    callback
  });
};

export const getWorkflowSnapshot = ({ workflowId, snapshotId }, callback) => {
  return get(`v1/workflows/${workflowId}/snapshots/${snapshotId}`, {
    action: 'GET_WORKFLOW_SNAPSHOT',
    meta: {
      workflowId,
      snapshotId
    },
    callback
  });
};

export const postWorkflowSnapshot = ({ workflowId, snapshot, changeCount }, callback) => {
  return post(`v1/workflows/${workflowId}/snapshots`, {
    action: 'PUT_WORKFLOW_SNAPSHOT',
    data: { snapshot, changeCount },
    meta: { workflowId },
    callback
  });
};

export const putWorkflowSnapshot = ({ workflowId, snapshotId, snapshot, replace, isDraft }, callback) => {
  return put(`v1/workflows/${workflowId}/snapshots/${snapshotId}`, {
    action: 'PUT_WORKFLOW_SNAPSHOT',
    data: {
      snapshot,
      replace,
      isDraft
    },
    meta: {
      workflowId,
      snapshotId
    },
    callback
  });
};

export const postWorkflowAutosave = ({ workflowId }, callback) => {
  return post(`v1/workflows/${workflowId}/autosaves`, {
    action: 'POST_WORKFLOW_AUTOSAVE',
    meta: { workflowId },
    loader: 'workflowAutosave',
    callback
  });
};

export const getWorkflowAutosaves = ({ workflowId, limit = 30 }, callback) => {
  return get(`v1/workflows/${workflowId}/autosaves`, {
    action: 'GET_WORKFLOW_AUTOSAVES',
    loader: 'getWorkflowAutosaves',
    query: { limit },
    meta: { workflowId },
    callback
  });
};

export const restoreWorkflowAutosave = ({ workflowId, autosaveId }, callback) => {
  return post(`v1/workflows/${workflowId}/autosaves/${autosaveId}/restorations`, {
    action: 'RESTORE_WORKFLOW_AUTOSAVE',
    meta: { workflowId, autosaveId },
    callback
  });
};

export const getLatestWorkflowLogs = (callback) => {
  return get(`v1/workflows/logs/latest`, {
    action: 'GET_LATEST_WORKFLOW_LOGS',
    callback
  });
};

export const postAccessTokenRefresh = ({ appId, appVersion, credentialId, companyId }, callback) => {
  return post(`v1/apps/${appId}/${appVersion}/access-token-refresh`, {
    query: { companyId },
    data: {
      credentialId
    },
    action: 'POST_ACCESS_TOKEN_REFRESH',
    callback
  });
};

export const getUserWorkflowAlerts = ({ workflowId }, callback) => {
  return get(`v1/workflows/${workflowId}/alerts`, {
    action: 'PUT_USER_WORKFLOW_ALERT',
    callback
  });
};

export const putUserWorkflowAlert = ({ workflowId, alertType, options, isActive }, callback) => {
  return put(`v1/workflows/${workflowId}/alerts`, {
    data: {
      alertType,
      options,
      isActive
    },
    action: 'PUT_USER_WORKFLOW_ALERT',
    callback
  });
};

export const setWorkflowMemory = (data, callback) => {
  return post(`v1/workflows/${data?.workflowId}/memory`, {
    data,
    action: 'SET_WORKFLOW_MEMORY',
    meta: { workflowId: data?.workflowId, environmentId: data?.environmentId },
    callback
  });
};

export const deleteWorkflowMemory = ({ workflowId, key, scope, environmentId, installationId }, callback) => {
  return del(`v1/workflows/${workflowId}/memory`, {
    action: 'DELETE_WORKFLOW_MEMORY',
    data: {
      key,
      scope,
      environmentId,
      installationId
    },
    meta: { workflowId, environmentId },
    loader: `deleteWorkflowMemory-${key}-${scope}`,
    callback
  });
};

export const deleteWorkflowLog = ({ workflowId, workflowLogId }, callback) => {
  return del(`v1/workflows/${workflowId}/logs/${workflowLogId}`, {
    action: 'DELETE_WORKFLOW_LOG',
    meta: { workflowId, workflowLogId },
    callback
  });
};

export const runStepHook = ({ workflowId, stepId, actionId, hook, callback }) => {
  return put(`v1/workflows/${workflowId}/steps/${stepId}/hooks/${hook}`, {
    data: {
      stepId,
      actionId
    },
    callback
  });
};

export const runStepAppDeploy = ({ workflowId, stepId, entryPoint, environmentId }, callback) => {
  return put(`v1/workflows/${workflowId}/steps/${stepId}/deploy`, {
    data: {
      entryPoint,
      environmentId
    },
    callback
  });
};

export const parseImportSettings = ({ appId, appVersion, type, callback, data, actionId }) => {
  return post(`v1/apps/${appId}/${appVersion}/parse-import-settings`, {
    query: { type, actionId },
    data: { data },
    action: 'POST_PARSE_IMPORT_SETTINGS',
    callback
  });
};

export const publishApp = ({ appId, appVersion = 'draft', versionIncrease, description }, callback) => {
  return post(`v1/apps/${appId}/${appVersion}/publish`, {
    action: 'PUBLISH_APP',
    data: {
      versionIncrease,
      description
    },
    meta: { appId, appVersion, versionIncrease },
    callback
  });
};

export const postAiAction = ({ action, context, options, cancelRef }, callback) => {
  return post(`v1/ai/action/${action}`, {
    data: { context, options },
    action: 'POST_AI_ACTION',
    callback,
    cancelRef
  });
};

export const getAppActionAttributes = ({ appId, useAi, appVersion, actionId }) => {
  return get(`v1/ai/app-action-attributes`, {
    query: {
      useAi,
      appId,
      appVersion,
      actionId
    }
  });
};

export const putSyncBatch = ({ batch, callback }) => {
  return put(`v1/sync/batch`, {
    query: {},
    data: { batch },
    action: 'PUT_SYNC_BATCH',
    callback
  });
};

export const putStepOutputResponse = ({ workflowId, stepId, newResponse, actionId }) => {
  return put(`v1/workflows/${workflowId}/steps/${stepId}/response`, {
    data: {
      newResponse,
      actionId
    }
  });
};

export const postPrivateBetaEmail = ({ email }) => {
  return post(`v1/private-beta`, {
    data: { email },
    action: 'POST_PRIVATE_BETA_EMAIL'
  });
};

export const postWorkflowTag = ({ id, label, color }, callback) => {
  return post(`v1/workflow-tags`, {
    data: { id, label, color },
    action: 'POST_WORKFLOW_TAG',
    callback
  });
};

export const putWorkflowTag = ({ id, label, color }, callback) => {
  return put(`v1/workflow-tags/${id}`, {
    data: { id, label, color },
    action: 'PUT_WORKFLOW_TAG',
    callback
  });
};

export const deleteWorkflowTag = ({ tagId }, callback) => {
  return del(`v1/workflow-tags/${tagId}`, {
    action: 'DELETE_WORKFLOW_TAG',
    meta: { tagId },
    callback
  });
};

export const applyRecipe = ({ recipeId, newWorkflowId }, callback) => {
  return post(`v1/recipes/apply`, {
    data: { recipeId, newWorkflowId },
    callback
  });
};

export const getM2MOauthToken = ({ appSlug, credentialId, codeVerifier, companyId, draft }, callback) => {
  return get(`v1/oauth/${appSlug}/m2m/token`, {
    query: { credentialId, companyId, codeVerifier, draft },
    action: 'GET_M2M_OAUTH_TOKEN',
    loader: `getM2MOauthToken-${credentialId}`,
    callback
  });
};

export const getCompaniesBySearch = ({ search, cancelRef }, callback) => {
  return get(`v1/companies/search`, {
    query: { search },
    action: 'GET_COMPANIES_BY_SEARCH',
    loader: 'getCompaniesBySearch',
    cancelRef,
    callback
  });
};

export const getCompaniesByIds = ({ ids }, callback) => {
  return get(`v1/companies/by-ids`, {
    query: { ids },
    action: 'GET_COMPANIES_BY_IDS',
    loader: 'getCompaniesByIds',
    callback
  });
};

export const getDocContent = ({ docId }, callback) => {
  return get(`v1/docs/${docId}`, {
    action: 'GET_DOC_CONTENT',
    loader: 'getDocContent',
    callback
  });
};

export const getAppDocContent = ({ appId, type, objectId, version }, callback) => {
  return get(`v1/docs/app/${appId}/${type}/${objectId}`, {
    action: 'GET_APP_DOC_CONTENT',
    loader: 'getAppDocContent',
    query: version ? { version } : undefined,
    callback
  });
};

export const getAppDocMeta = ({ appId, type, objectId }, callback) => {
  return get(`v1/docs/app/${appId}/${type}/${objectId}/meta`, {
    action: 'GET_APP_DOC_META',
    loader: 'getAppDocMeta',
    callback
  });
};

export const putAppDocContent = ({ appId, type, objectId, userContext, version }, callback) => {
  return put(`v1/docs/app/${appId}/${type}/${objectId}`, {
    data: { userContext, version },
    action: 'PUT_APP_DOC_CONTENT',
    callback
  });
};

export const postAiChatbot = ({ messages }, callback) => {
  return post(`v1/ai/chatbot`, {
    data: { messages },
    action: 'POST_AI_CHAT_MESSAGE',
    loader: 'postAiChatbot',
    callback
  });
};

export const swapTempCredCompany = ({ appId, tempCompanyId, credentialId }, callback) => {
  return post(`v1/credentials/swap`, {
    data: {
      appId,
      tempCompanyId,
      credentialId
    },
    action: 'SWAP_TEMP_CRED_COMPANY',
    callback
  });
};

export const postAppRequest = ({ name, description, companyName, userName, userEmail, type, appSlug }, callback) => {
  // workflow id: FqrT7FgS8D6Y
  return post('https://webhook.mindcloud.co/v1/webhook/073842307b2498c678647f0b5584c8c87d2bd66731b8714ed960220a51f504fd372604d873408dcab69b012a4cc8e8f3', {
    data: { name, description, companyName, userName, userEmail, type, appSlug },
    action: 'POST_APP_REQUEST',
    callback,
    loader: 'postAppRequest'
  });
};

export const postPlatformIssue = ({ companyName, userName, userEmail, message, tutorialSlug }, callback) => {
  // workflow id: UhaDDJa9nx4c
  return post('https://webhook.mindcloud.co/v1/webhook/60da9889fdaced488de5d674a9545a6a5395e07929c005e2ee64667a934e2dc84fd160bd2005045d4460d2bc30af13c8', {
    data: { companyName, userName, userEmail, message, tutorialSlug },
    callback
  });
};

export const messageTeamMemberOnSlack = ({ channelName, teamMemberSlackUserId }, callback) => {
  // workflow id: OY6TXu066sfm
  return post('https://webhook.mindcloud.co/v1/webhook/b52ce85821f122a5b8fdb38aefc0358d0d2cbd442cce11c916e95346437ef820b447d7d3f9814fd8d93a6afff3fdaf61', {
    data: { channelName, teamMemberSlackUserId },
    callback
  });
};

export const getWorkflowStepComments = ({ workflowId }, callback) => {
  return get(`v1/workflows/${workflowId}/steps/comments`, {
    action: 'GET_WORKFLOW_STEP_COMMENTS',
    meta: { workflowId },
    callback
  });
};

export const postWorkflowStepComment = ({ workflowId, stepId, comment, mentions }, callback) => {
  return post(`v1/workflows/${workflowId}/steps/${stepId}/comments`, {
    data: { comment, mentions },
    action: 'POST_WORKFLOW_STEP_COMMENT',
    loader: 'postWorkflowStepComment',
    meta: { workflowId, stepId },
    callback
  });
};

export const putWorkflowStepComment = ({ workflowId, stepId, commentId, comment, mentions }, callback) => {
  return put(`v1/workflows/${workflowId}/steps/${stepId}/comments/${commentId}`, {
    data: { comment, mentions },
    action: 'PUT_WORKFLOW_STEP_COMMENT',
    meta: { workflowId, stepId, commentId },
    callback
  });
};

export const deleteWorkflowStepComment = ({ workflowId, stepId, commentId }, callback) => {
  return del(`v1/workflows/${workflowId}/steps/${stepId}/comments/${commentId}`, {
    action: 'DELETE_WORKFLOW_STEP_COMMENT',
    meta: { workflowId, stepId, commentId },
    callback
  });
};

export const getWorkflowStepWebhookResponses = ({ workflowId, stepId }, callback) => {
  return get(`v1/workflows/${workflowId}/steps/${stepId}/webhook-responses`, {
    action: 'GET_WORKFLOW_STEP_WEBHOOK_RESPONSES',
    loader: 'getWorkflowStepWebhookResponses',
    callback,
    meta: { stepId }
  });
};

export const rerunWebhook = ({ workflowId, workflowLogId, webhookStepId }, callback) => {
  return post(`v1/webhook/rerun`, {
    action: 'RERUN_WEBHOOK',
    loader: 'rerunWebhook',
    data: { workflowId, workflowLogId, webhookStepId },
    meta: { workflowId, workflowLogId, webhookStepId },
    callback
  });
};

export const getWorkflowStepLongPollResponses = ({ workflowId, stepId, isFetch = false }, callback) => {
  return get(`v1/workflows/${workflowId}/steps/${stepId}/long-poll-responses`, {
    action: 'GET_WORKFLOW_STEP_LONG_POLL_RESPONSES',
    query: { isFetch },
    loader: 'getWorkflowStepLongPollResponses',
    callback,
    meta: { stepId }
  });
};

export const applyWorkflowStepLongPollResponse = ({ workflowId, stepId, response }, callback) => {
  return post(`v1/workflows/${workflowId}/steps/${stepId}/long-poll-responses/apply`, {
    data: { response },
    action: 'APPLY_WORKFLOW_STEP_LONG_POLL_RESPONSE',
    callback
  });
};

export const applyWorkflowStepWebhookResponse = ({ workflowId, workflowVersion, stepId, slug }, callback) => {
  return post(`v1/workflows/${workflowId}/${workflowVersion}/steps/${stepId}/webhook-responses/apply`, {
    data: { slug },
    action: 'APPLY_WORKFLOW_STEP_WEBHOOK_RESPONSE',
    callback
  });
};

export const uploadImages = ({ files, path = '' }) => {
  return post(`v1/upload/images`, {
    data: files,
    path,
    action: 'UPLOAD_IMAGES',
    loader: 'uploadImages',
    headers: { 'Content-Type': 'multipart/form-data', path }
  });
};

export const uploadDocuments = ({ files }) => {
  return post(`v1/upload/documents`, {
    data: files,
    action: 'UPLOAD_DOCUMENTS',
    loader: 'uploadDocuments',
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getAiSuggestions = () => {
  return get(`v1/ai/suggestions`, {
    action: 'GET_AI_SUGGESTIONS',
    loader: 'getAiSuggestions'
  });
};

export const updateAiSuggestion = ({ id, path, action, meta }) => {
  return post(`v1/ai/suggestions`, {
    data: { id, path, action, meta },
    action: 'UPDATE_AI_SUGGESTION'
  });
};

export const putAppActionResponse = ({ appId, appVersion, actionId, statusId, newResponse }, callback) => {
  return put(`v1/apps/${appId}/${appVersion}/action/${actionId}/response`, {
    data: { newResponse, statusId },
    action: 'GET_APP_ACTION_OUTPUT',
    callback
  });
};

export const getDomainDiscovery = ({ domain }) => {
  return get(`v1/companies/domain-discovery`, {
    query: { domain },
    action: 'GET_DOMAIN_DISCOVERY',
    loader: 'getDomainDiscovery'
  });
};

export const joinCompanyFromDiscovery = ({ companyId }) => {
  return post(`v1/companies/join-from-discovery`, {
    data: { companyId },
    action: 'JOIN_COMPANY_FROM_DISCOVERY'
  });
};

export const getAppDefects = ({ appId, statuses }, callback) => {
  return get(`v1/apps/${appId}/defects`, {
    action: 'GET_APP_DEFECTS',
    loader: 'getAppDefects',
    query: statuses ? { statuses } : {},
    meta: { appId },
    callback
  });
};

export const reportAppDefect = ({ appId, actionId, category, reportText }, callback) => {
  return post(`v1/apps/${appId}/defects`, {
    action: 'REPORT_APP_DEFECT',
    loader: 'reportAppDefect',
    data: { actionId, category, reportText },
    meta: { appId },
    callback
  });
};

export const getAppFixDefects = ({ statuses, limit } = {}, callback) => {
  return get(`v1/app-fixes/defects`, {
    action: 'GET_APP_FIX_DEFECTS',
    loader: 'getAppFixDefects',
    query: cleanObject({ statuses, limit }),
    callback
  });
};

export const getAppFixProposals = ({ status, defectId } = {}, callback) => {
  return get(`v1/app-fixes/proposals`, {
    action: 'GET_APP_FIX_PROPOSALS',
    loader: 'getAppFixProposals',
    query: cleanObject({ status, defectId }),
    callback
  });
};

export const startAppFixDefect = ({ defectId, userPrompt }, callback) => {
  return post(`v1/app-fixes/defects/${defectId}/start`, {
    action: 'START_APP_FIX_DEFECT',
    loader: 'startAppFixDefect',
    data: { userPrompt },
    callback
  });
};

export const dismissAppFixDefect = ({ defectId }, callback) => {
  return post(`v1/app-fixes/defects/${defectId}/dismiss`, {
    action: 'DISMISS_APP_FIX_DEFECT',
    loader: 'dismissAppFixDefect',
    callback
  });
};

export const approveAppFixProposal = ({ proposalId }, callback) => {
  return post(`v1/app-fixes/proposals/${proposalId}/approve`, {
    action: 'APPROVE_APP_FIX_PROPOSAL',
    loader: 'approveAppFixProposal',
    callback
  });
};

export const rejectAppFixProposal = ({ proposalId, comment, mode }, callback) => {
  return post(`v1/app-fixes/proposals/${proposalId}/reject`, {
    action: 'REJECT_APP_FIX_PROPOSAL',
    loader: 'rejectAppFixProposal',
    data: { comment, mode },
    callback
  });
};

export const testCredential = ({ credentialId, appId, appVersion }) => {
  return post(`v1/credentials/${credentialId}/test`, {
    data: { appId, appVersion }
  });
};

export const addWorkflowToIntegration = ({ integrationId, workflowId }, callback) => {
  return post(`v1/embedded/integrations/${integrationId}/workflows`, {
    data: { workflowId },
    action: 'ADD_WORKFLOW_TO_INTEGRATION',
    loader: 'addWorkflowToIntegration',
    callback
  });
};

export const removeWorkflowFromIntegration = ({ integrationId, workflowId }, callback) => {
  return del(`v1/embedded/integrations/${integrationId}/workflows/${workflowId}`, {
    action: 'REMOVE_WORKFLOW_FROM_INTEGRATION',
    loader: 'removeWorkflowFromIntegration',
    callback
  });
};

export const getIntegrationsMetadata = (callback) => {
  return get(`v1/embedded/integrations`, {
    action: 'GET_INTEGRATIONS_METADATA',
    loader: 'getIntegrationsMetadata',
    callback
  });
};

export const getCirraWorkflows = (callback) => {
  return get(`v1/cirra/workflows`, {
    action: 'GET_CIRRA_WORKFLOWS',
    loader: 'getCirraWorkflows',
    callback
  });
};

export const createIntegration = ({ name, appId }, callback) => {
  return post(`v1/embedded/integrations`, {
    data: { name, appId },
    action: 'CREATE_INTEGRATION',
    loader: 'createIntegration',
    callback
  });
};

export const updateIntegration = ({ integrationId, name, description, appId, appName, usesEmbeddedApp, settings }, callback) => {
  return put(`v1/embedded/integrations/${integrationId}`, {
    data: { name, description, appId, appName, usesEmbeddedApp, settings },
    action: 'UPDATE_INTEGRATION',
    loader: 'updateIntegration',
    callback
  });
};

export const deleteIntegration = ({ integrationId }, callback) => {
  return del(`v1/embedded/integrations/${integrationId}`, {
    action: 'DELETE_INTEGRATION',
    loader: 'deleteIntegration',
    callback
  });
};

export const getIntegration = ({ integrationId, endUserId }, callback) => {
  return get(`v1/embedded/integrations/${integrationId}`, {
    action: 'GET_INTEGRATION',
    loader: 'getIntegration',
    query: { endUserId },
    callback,
    meta: { integrationId }
  });
};

export const renameIntegrationApp = ({ integrationId, integrationAppId, name }, callback) => {
  return put(`v1/embedded/integrations/${integrationId}/apps/${integrationAppId}`, {
    data: { name },
    action: 'RENAME_INTEGRATION_APP',
    loader: 'renameIntegrationApp',
    callback,
    meta: { integrationId }
  });
};

export const deleteIntegrationApp = ({ integrationId, integrationAppId }, callback) => {
  return del(`v1/embedded/integrations/${integrationId}/apps/${integrationAppId}`, {
    action: 'DELETE_INTEGRATION_APP',
    loader: 'deleteIntegrationApp',
    callback,
    meta: { integrationId }
  });
};

export const getMockTypes = ({ ids }) => {
  return get(`v1/mock-types`, {
    query: { ids },
    action: 'GET_MOCK_TYPES',
    loader: 'getMockTypes'
  });
};

export const getNetsuiteRecordSchema = ({ recordType, realmId, workflowId, stepId, environmentId, cancelRef }, callback) => {
  return get(`v1/apps/netsuite/record-schema`, {
    query: { recordType, realmId, workflowId, stepId, environmentId },
    meta: { recordType, realmId },
    action: 'GET_NETSUITE_RECORD_SCHEMA',
    loader: 'getNetsuiteRecordSchema',
    cancelRef,
    callback
  });
};

export const getHealth = ({ startTime, endTime, type, cancelRef }, callback) => {
  return get(`v1/monitoring/health`, {
    query: { startTime, endTime, type },
    action: 'GET_HEALTH',
    loader: 'getHealth',
    callback,
    cancelRef
  });
};

export const getMonitoringErrors = ({ startTime, endTime, type, cancelRef }, callback) => {
  return get(`v1/monitoring/errors`, {
    query: { startTime, endTime, type },
    action: 'GET_MONITORING_ERRORS',
    loader: 'getMonitoringErrors',
    callback,
    cancelRef
  });
};

export const getCompletedByTrigger = ({ startTime, endTime, cancelRef }, callback) => {
  return get(`v1/monitoring/completed-by-trigger`, {
    query: { startTime, endTime },
    action: 'GET_COMPLETED_BY_TRIGGER',
    loader: 'getCompletedByTrigger',
    callback,
    cancelRef
  });
};

export const getIntegrationsOverview = ({ integrator, pmId, cancelRef }, callback) => {
  const query = {};

  if (integrator) {
    query.integrator = integrator;
  }

  if (pmId) {
    query.pmId = pmId;
  }

  return get(`v1/projects/integrations-overview`, {
    query,
    action: 'GET_INTEGRATIONS_OVERVIEW',
    loader: 'getIntegrationsOverview',
    callback,
    cancelRef
  });
};

export const getMaintenanceOverview = (callback) => {
  return get(`v1/projects/maintenance`, {
    action: 'GET_MAINTENANCE_OVERVIEW',
    loader: 'getMaintenanceOverview',
    callback
  });
};

export const syncProjects = ({ fullSync = false }, callback) => {
  return post(`v1/projects/syncProjects`, {
    action: 'SYNC_PROJECTS',
    loader: 'syncProjects',
    data: { fullSync },
    callback
  });
};

export const syncIssues = ({ fullSync = false }, callback) => {
  return post(`v1/projects/syncIssues`, {
    action: 'SYNC_ISSUES',
    loader: 'syncIssues',
    data: { fullSync },
    callback
  });
};

export const getIntegrationsProjects = ({ type, status, integrator, pmId, showAllStatuses, cancelRef }, callback) => {
  return get(`v1/projects/integrations-projects`, {
    query: { type, status, integrator, pmId, showAllStatuses },
    action: 'GET_INTEGRATIONS_PROJECTS',
    loader: 'getIntegrationsProjects',
    callback,
    cancelRef
  });
};

export const getIntegrationsProject = ({ projectId, cancelRef }, callback) => {
  return get(`v1/projects/integrations-projects`, {
    action: 'GET_INTEGRATIONS_PROJECT',
    query: { projectId },
    loader: 'getIntegrationsProject',
    callback,
    cancelRef,
    meta: { projectId }
  });
};

export const getCompanyIntegrationsProjects = ({ cancelRef } = {}, callback) => {
  return get(`v1/projects/company-integrations-projects`, {
    action: 'GET_COMPANY_INTEGRATIONS_PROJECTS',
    loader: 'getCompanyIntegrationsProjects',
    callback,
    cancelRef
  });
};

export const getIntegrators = ({ cancelRef }, callback) => {
  return get(`v1/projects/integrators`, {
    action: 'GET_INTEGRATORS',
    loader: 'getIntegrators',
    callback,
    cancelRef
  });
};

export const getServiceUsers = ({ roles, cancelRef }, callback) => {
  return get(`v1/projects/service-users`, {
    action: 'GET_SERVICE_USERS',
    loader: 'getServiceUsers',
    callback,
    cancelRef,
    query: { roles: roles.join(',') }
  });
};

export const getIntegratorsProjects = ({ cancelRef }, callback) => {
  return get(`v1/projects/integrators-projects`, {
    action: 'GET_INTEGRATORS_PROJECTS',
    loader: 'getIntegratorsProjects',
    callback,
    cancelRef
  });
};

export const getIntegratorsIssues = ({ cancelRef }, callback) => {
  return get(`v1/projects/integrators-issues`, {
    action: 'GET_INTEGRATORS_ISSUES',
    loader: 'getIntegratorsIssues',
    callback,
    cancelRef
  });
};

export const getIntegrationGraphs = ({ startTime, endTime, cancelRef }, callback) => {
  return get(`v1/projects/graphs`, {
    action: 'GET_INTEGRATION_GRAPHS',
    loader: 'getIntegrationGraphs',
    callback,
    cancelRef,
    query: { startTime, endTime }
  });
};

export const getAllIssues = ({ cancelRef, pmId, integrator, projectId, issueStatus, issueStatuses, type, metric, startDate, endDate, showAllStatuses }, callback) => {
  return get(`v1/projects/issues`, {
    action: 'GET_ALL_ISSUES',
    loader: 'getAllIssues',
    callback,
    cancelRef,
    query: cleanObject({ pmId, integrator, projectId, issueStatus, issueStatuses, type, metric, startDate, endDate, showAllStatuses })
  });
};

export const getCompanyWorkflows = ({ companyId, search, cancelRef }, callback) => {
  return get(`v1/workflows/company/${companyId}`, {
    action: 'GET_COMPANY_WORKFLOWS',
    loader: 'getCompanyWorkflows',
    query: { search },
    callback,
    cancelRef
  });
};

export const searchForWorkflowToRestore = ({ workflowId }, callback) => {
  return get(`v1/workflows/restore`, {
    query: { workflowId },
    action: 'SEARCH_FOR_WORKFLOW_TO_RESTORE',
    loader: 'searchForWorkflowToRestore',
    callback
  });
};

export const restoreWorkflow = ({ workflowId, key, stepsKey }, onResponse) => {
  return post(`v1/workflows/restore`, {
    data: { workflowId, key, stepsKey },
    action: 'RESTORE_WORKFLOW',
    loader: 'restoreWorkflow',
    onResponse
  });
};

export const updateInstallationMetadata = ({ installationId, metadata }, callback) => {
  return put(`v1/embedded/installation/${installationId}/metadata`, {
    data: { metadata },
    action: 'UPDATE_INSTALLATION_METADATA',
    loader: 'updateInstallationMetadata',
    callback,
    meta: { installationId, metadata }
  });
};

export const searchEndUsers = ({ search, cancelRef }, callback) => {
  return get(`v1/embedded/end-users`, {
    query: { search },
    action: 'SEARCH_END_USERS',
    loader: 'searchEndUsers',
    callback,
    cancelRef
  });
};

export const generateIntegrationShareLink = ({ integrationId, email, expirationTime }, callback) => {
  return post(`v1/embedded/integrations/${integrationId}/links`, {
    data: { email, expirationTime },
    action: 'GENERATE_INTEGRATION_SHARE_LINK',
    loader: 'generateIntegrationShareLink',
    callback
  });
};

export const getIntegrationFromShareLink = ({ integrationId, token }, callback) => {
  return get(`v1/embedded/integrations/shared`, {
    query: { integrationId, token },
    action: 'GET_INTEGRATION_FROM_SHARE_LINK',
    loader: 'getIntegrationFromShareLink',
    callback
  });
};

export const validateEmbeddedToken = ({ token }, callback) => {
  return post(`v1/auth/validate-embedded-token`, {
    data: { token },
    action: 'VALIDATE_EMBEDDED_TOKEN',
    loader: 'validateEmbeddedToken',
    callback
  });
};

export const upsertProvisionedCredential = ({ provisioningEndpoint, appId, endUserId, testVariables }, callback) => {
  return put(`v1/embedded/credentials/provisioned`, {
    data: { provisioningEndpoint, appId, endUserId, testVariables },
    action: 'UPSERT_PROVISIONED_CREDENTIAL',
    loader: 'upsertProvisionedCredential',
    callback
  });
};

export const getAuditLogs = ({ id, page, limit, search, type }, callback) => {
  return get(`v1/audit-logs/${id}`, {
    action: 'GET_AUDIT_LOGS',
    loader: 'getAuditLogs',
    query: { page, limit, search, type },
    callback
  });
};

export const getCompanyEmbeddedApp = (callback) => {
  return get(`v1/embedded/company/app`, {
    action: 'GET_COMPANY_EMBEDDED_APP',
    loader: 'getCompanyEmbeddedApp',
    callback
  });
};

export const getWorkflowBlueprintShareLink = ({ issueId }, callback) => {
  return post(`v1/workflow-blueprint/issues/${issueId}/share-link`, {
    action: 'GET_WORKFLOW_BLUEPRINT_SHARE_LINK',
    loader: 'getWorkflowBlueprintShareLink',
    callback
  });
};

export const regenerateWorkflowBlueprintShareLink = ({ issueId }, callback) => {
  return post(`v1/workflow-blueprint/issues/${issueId}/share-link/regenerate`, {
    action: 'REGENERATE_WORKFLOW_BLUEPRINT_SHARE_LINK',
    loader: 'regenerateWorkflowBlueprintShareLink',
    callback
  });
};

export const sendAdditionalRequirementsEmail = ({ issueId, contacts, questions }, callback) => {
  return post(`v1/workflow-blueprint/issues/${issueId}/send-additional-requirements`, {
    action: 'SEND_ADDITIONAL_REQUIREMENTS_EMAIL',
    data: { contacts, questions },
    loader: 'sendAdditionalRequirementsEmail',
    callback
  });
};

export const getWorkflowBlueprintShared = ({ token }, callback) => {
  return get(`v1/workflow-blueprint/shared`, {
    query: { token },
    action: 'GET_WORKFLOW_BLUEPRINT_SHARED',
    loader: 'getWorkflowBlueprintShared',
    callback
  });
};

export const getWorkflowBlueprintSharedIssues = ({ token }, callback) => {
  return get(`v1/workflow-blueprint/shared/issues`, {
    query: { token },
    action: 'GET_WORKFLOW_BLUEPRINT_SHARED_ISSUES',
    loader: 'getWorkflowBlueprintSharedIssues',
    callback
  });
};

export const getWorkflowBlueprintSharedApps = ({ token }, callback) => {
  return get(`v1/workflow-blueprint/shared/apps`, {
    query: { token },
    action: 'GET_WORKFLOW_BLUEPRINT_SHARED_APPS',
    loader: 'getWorkflowBlueprintSharedApps',
    callback
  });
};

export const getWorkflowBlueprintSharedMembers = ({ token }, callback) => {
  return get(`v1/workflow-blueprint/shared/members`, {
    query: { token },
    action: 'GET_WORKFLOW_BLUEPRINT_SHARED_MEMBERS',
    loader: 'getWorkflowBlueprintSharedMembers',
    callback
  });
};

export const getWorkflowBlueprintSharedContacts = ({ token }, callback) => {
  return get(`v1/workflow-blueprint/shared/contacts`, {
    query: { token },
    action: 'GET_WORKFLOW_BLUEPRINT_SHARED_CONTACTS',
    loader: 'getWorkflowBlueprintSharedContacts',
    callback
  });
};

export const getWorkflowBlueprintSharedLink = ({ token, issueId }, callback) => {
  return post(`v1/workflow-blueprint/shared/link`, {
    data: { token, issueId },
    action: 'GET_WORKFLOW_BLUEPRINT_SHARED_LINK',
    loader: 'getWorkflowBlueprintSharedLink',
    callback
  });
};

export const getWorkflowBlueprintSharedPlatformSecrets = ({ token, appId, search }, callback) => {
  return get(`v1/workflow-blueprint/shared/platform-secrets`, {
    query: { token, appId, search },
    action: 'GET_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRETS',
    loader: `getWorkflowBlueprintSharedPlatformSecrets-${appId}`,
    meta: { appId },
    callback
  });
};

export const getWorkflowBlueprintSharedPlatformSecretById = ({ token, id }, callback) => {
  return get(`v1/workflow-blueprint/shared/platform-secrets/${id}`, {
    query: { token },
    action: 'GET_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRET',
    loader: `getWorkflowBlueprintSharedPlatformSecretById-${id}`,
    meta: { id },
    callback
  });
};

export const postWorkflowBlueprintSharedPlatformSecret = ({ token, appId, label, environment, secrets }, callback) => {
  return post(`v1/workflow-blueprint/shared/platform-secrets`, {
    data: { token, appId, label, environment, secrets },
    action: 'PUT_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRET',
    callback
  });
};

export const putWorkflowBlueprintSharedPlatformSecret = ({ token, id, appId, label, environment, secrets }, callback) => {
  return put(`v1/workflow-blueprint/shared/platform-secrets/${id}`, {
    data: { token, appId, label, environment, secrets },
    action: 'PUT_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRET',
    callback
  });
};

export const deleteWorkflowBlueprintSharedPlatformSecret = ({ token, id }, callback) => {
  return del(`v1/workflow-blueprint/shared/platform-secrets/${id}`, {
    query: { token },
    action: 'DELETE_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRET',
    meta: { id },
    callback
  });
};

export const putWorkflowBlueprintSharedUpdate = ({ token, path, value }, callback) => {
  return put(`v1/workflow-blueprint/shared`, {
    data: { token, path, value },
    action: 'PUT_WORKFLOW_BLUEPRINT_SHARED_UPDATE',
    loader: 'putWorkflowBlueprintSharedUpdate',
    callback
  });
};

export const getEmbeddedUserToken = (callback) => {
  return get(`v1/embedded/end-users/test/token`, {
    action: 'GET_EMBEDDED_USER_TOKEN',
    loader: 'getEmbeddedUserToken',
    callback
  });
};

/* export const migrateWorkflowMeta = () => {
  return get(`v1/workflows/migrate-meta`, {
    action: 'MIGRATE_WORKFLOW_META',
    loader: 'migrateWorkflowMeta'
  });
}; */

export const getMindCloudEmbeddedUserToken = (callback) => {
  return get(`v1/auth/embedded/mindcloud/end-user/token`, {
    action: 'GET_MIND_CLOUD_EMBEDDED_USER_TOKEN',
    loader: 'getMindCloudEmbeddedUserToken',
    callback
  });
};

export const createTurnkeyEndUser = (callback) => {
  return post(`v1/turnkey/end-user`, {
    action: 'CREATE_TURNKEY_END_USER',
    loader: 'createTurnkeyEndUser',
    callback
  });
};

export const getMindCloudTurnkeyUserToken = (callback) => {
  return get(`v1/auth/turnkey/end-user/token`, {
    action: 'GET_MIND_CLOUD_TURNKEY_USER_TOKEN',
    loader: 'getMindCloudTurnkeyUserToken',
    callback
  });
};

export const getG2ReviewState = (callback) => {
  return get(`v1/g2/review-state`, {
    action: 'GET_G2_REVIEW_STATE',
    loader: 'getG2ReviewState',
    callback
  });
};

export const deleteInstallation = ({ installationId, integrationId }, callback) => {
  return del(`v1/embedded/installation/${installationId}`, {
    action: 'DELETE_INSTALLATION',
    loader: 'deleteInstallation',
    callback,
    meta: { installationId, integrationId }
  });
};

export const getEcsDeployments = ({ startTime, endTime, cancelRef }, callback) => {
  return get(`v1/monitoring/ecs-deployments`, {
    query: { startTime, endTime },
    action: 'GET_ECS_DEPLOYMENTS',
    loader: 'getEcsDeployments',
    callback,
    cancelRef
  });
};

export const buildAuthorizeUrl = ({ appSlug, credentialId, companyId, query, draft, embeddedToken }, callback) => {
  return get(`v1/oauth/authorize-url`, {
    query: { appSlug, credentialId, companyId, query, draft },
    // Embedded end-users have no Clerk session; forward their token so the server
    // can prove they own companyId before it reads the credential's secrets.
    ...(embeddedToken ? { headers: { Authorization: `Bearer ${embeddedToken}` } } : {}),
    action: 'GET_AUTHORIZE_URL',
    loader: 'getAuthorizeUrl',
    callback
  });
};

export const getApiCalls = ({ month }, callback) => {
  return get(`v1/monitoring/api-calls`, {
    query: { month },
    action: 'GET_API_CALLS',
    loader: 'getApiCalls',
    callback
  });
};

export const getWorkflowOperations = ({ companyId, month }, callback) => {
  return get(`v1/monitoring/workflow-operations`, {
    query: { companyId, month },
    action: 'GET_WORKFLOW_OPERATIONS',
    loader: 'getWorkflowOperations',
    callback,
    meta: { companyId, month }
  });
};

export const getWorkflowCompanyUsage = (callback) => {
  return get(`v1/monitoring/workflow-company-usage`, {
    action: 'GET_WORKFLOW_COMPANY_USAGE',
    loader: 'getWorkflowCompanyUsage',
    callback
  });
};
